import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  Eye,
  Scale,
  Network,
  FileText,
  Layers,
  Landmark,
  Users,
  ClipboardCheck,
  MessagesSquare,
  XCircle,
} from 'lucide-react';
import { colors } from '../styles/colors';
import { useFillout } from '../hooks/useFillout';

// ---------- Shared SectionHeading ----------
const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span
          className="text-[11px] uppercase tracking-[0.28em] font-medium"
          style={{ color: colors.teal }}
        >
          {eyebrow}
        </span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2
      className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight"
      style={{ color: colors.white }}
    >
      {title}
    </h2>
    {sub && (
      <p
        className="text-base sm:text-lg leading-relaxed"
        style={{ color: colors.white, opacity: 0.65 }}
      >
        {sub}
      </p>
    )}
  </div>
);

const LiquidityProvidersPage = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const navigate = useNavigate();
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroVisible(true));
    window.scrollTo(0, 0);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ---- Section 4: Reserve Framework pillars ----
  const frameworkPillars = [
    { Icon: ShieldCheck, label: 'Asset Verification' },
    { Icon: Eye, label: 'Reserve Visibility' },
    { Icon: ClipboardCheck, label: 'Collateral Administration' },
    { Icon: Users, label: 'Participant Transparency' },
    { Icon: FileText, label: 'Ongoing Reporting' },
  ];

  // ---- Section 5: Evaluation cards ----
  const evaluationCards = [
    {
      Icon: Layers,
      title: 'Reserve Composition',
      body: 'Understand the asset base supporting the reserve framework.',
    },
    {
      Icon: ClipboardCheck,
      title: 'Verification & Reporting',
      body: 'Review standards, documentation, reporting processes, and reserve transparency.',
    },
    {
      Icon: Network,
      title: 'Collateral Pathways',
      body: 'Assess how reserve-backed instruments support structured liquidity arrangements.',
    },
    {
      Icon: Scale,
      title: 'Governance Separation',
      body: 'Understand the distinction between corporate, reserve, marketplace, and interaction layers.',
    },
    {
      Icon: Landmark,
      title: 'Marketplace Separation',
      body: 'BTX operates marketplace participation independently from Arizore’s reserve framework.',
    },
  ];

  // ---- Section 6: Comparison ----
  const comparisonRows = [
    ['Limited asset visibility', 'Verified asset intake'],
    ['Fragmented documentation', 'Transparent reserve framework'],
    ['Difficult collateral assessment', 'Reserve data visibility'],
    ['Slow diligence', 'Defined participant roles'],
    ['Unclear participation pathways', 'Structured collateral pathways'],
  ];

  // ---- Section 9: Engagement flow ----
  const engagementSteps = [
    {
      step: '01',
      title: 'Initial Discussion',
      body: 'Understand liquidity provider requirements and participation criteria.',
      Icon: MessagesSquare,
    },
    {
      step: '02',
      title: 'Reserve & Collateral Review',
      body: 'Review reserve framework, reserve data, and collateral pathways.',
      Icon: Eye,
    },
    {
      step: '03',
      title: 'Structure Alignment',
      body: 'Assess suitable structured finance and collateral arrangements.',
      Icon: Layers,
    },
    {
      step: '04',
      title: 'Ongoing Reporting',
      body: 'Maintain visibility through reserve reporting and agreed communication pathways.',
      Icon: FileText,
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* ============================================================ */}
      {/* §1 HERO — Reserves asset                                       */}
      {/* ============================================================ */}
      <section
        className="relative overflow-hidden"
        style={{
          minHeight: '92vh',
          background:
            'linear-gradient(180deg, #000000 0%, #03090C 60%, #07141A 100%)',
        }}
      >
        <picture>
          <source srcSet="/lp-hero-reserves.webp" type="image/webp" />
          <img
            src="/lp-hero-reserves.jpg"
            alt=""
            aria-hidden="true"
            data-testid="lp-hero-image"
            className={`absolute inset-0 w-full h-full object-cover lp-hero-drift transition-opacity duration-[2000ms] ease-out ${
              heroVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ objectPosition: 'center center' }}
            loading="eager"
            decoding="async"
          />
        </picture>

        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.92) 100%)',
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(55% 60% at 50% 55%, rgba(0,128,128,0.10) 0%, rgba(0,0,0,0) 70%)',
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center min-h-[82vh] pt-28 pb-16 sm:pt-28 sm:pb-20">
          <div
            className={`transition-all duration-1000 ease-out transform ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}
          >
            <div className="flex items-center gap-3 mb-7 justify-center">
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
              <span
                className="text-[11px] uppercase tracking-[0.32em] font-medium"
                style={{ color: colors.teal, fontFamily: 'inherit' }}
              >
                For Liquidity Providers
              </span>
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-[-0.02em] mb-7 max-w-4xl mx-auto"
              style={{ color: colors.white, fontFamily: 'inherit', fontWeight: 700 }}
            >
              VERIFIED COLLATERAL FOR STRUCTURED LIQUIDITY.
            </h1>

            <p
              className="text-base sm:text-lg lg:text-xl leading-[1.55] max-w-2xl mx-auto mb-12"
              style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'inherit' }}
            >
              Arizore connects verified assets, reserve transparency, and liquidity partners through a structured reserve framework.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={openFilloutPopup}
                data-testid="lp-hero-speak-team-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{
                  backgroundColor: colors.teal,
                  color: colors.white,
                  border: `1px solid ${colors.teal}`,
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Speak With The Team
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/reserve-data/azg')}
                data-testid="lp-hero-view-reserve-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{
                  backgroundColor: 'transparent',
                  color: colors.white,
                  border: '1px solid rgba(255,255,255,0.25)',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                }}
              >
                View Reserve Data
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes lp-hero-drift {
            0%   { transform: scale(1.04) translate3d(0, 0, 0); }
            50%  { transform: scale(1.06) translate3d(-0.5%, -0.5%, 0); }
            100% { transform: scale(1.04) translate3d(0, 0, 0); }
          }
          .lp-hero-drift { animation: lp-hero-drift 24s ease-in-out infinite; will-change: transform; }
          @media (prefers-reduced-motion: reduce) { .lp-hero-drift { animation: none; } }
        `}</style>
      </section>

      {/* ============================================================ */}
      {/* §2 LIQUIDITY PROVIDER PROBLEM                                 */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: 'url(/home-bg-grid.webp)', backgroundSize: 'cover', backgroundPosition: 'center 20%', opacity: 0.1 }}
        />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.65) 50%, #040404 100%)' }} />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Liquidity Provider Problem"
            title="COLLATERAL QUALITY DEPENDS ON VISIBILITY."
            sub="Liquidity providers need confidence before capital moves. Many real-world assets hold significant value but remain difficult to assess, monitor, or structure as collateral within traditional frameworks."
          />
          <p
            className="max-w-2xl mx-auto text-center text-base sm:text-lg leading-relaxed mb-12 sm:mb-14"
            style={{ color: colors.white, opacity: 0.75 }}
          >
            The issue is not always asset value. The issue is{' '}
            <span style={{ color: colors.teal, fontWeight: 500 }}>visibility, verification, and structure</span>.
          </p>

          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {[
              'Verified assets are often difficult to mobilise.',
              'Traditional collateral frameworks can struggle with non-liquid assets.',
              'Transparency and reporting are essential to institutional participation.',
            ].map((point, idx) => (
              <div
                key={point}
                data-testid={`lp-problem-point-${idx + 1}`}
                className="p-6 sm:p-7 rounded-2xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <span className="text-xs font-semibold tracking-[0.12em]" style={{ color: colors.teal }}>{String(idx + 1).padStart(2, '0')}</span>
                </div>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.75 }}>{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* §3 WHY ARIZORE EXISTS                                         */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.16 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.55) 50%, #050505 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why Arizore Exists"
            title="A RESERVE FRAMEWORK BUILT AROUND VERIFICATION."
            sub="Arizore provides a transparent reserve framework designed to connect verified assets with structured liquidity pathways. The reserve creates a clearer environment for evaluating assets, understanding participant roles, and reviewing reserve composition."
          />

          {/* Key principles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-12">
            {[
              { Icon: ShieldCheck, label: 'Verified Asset Intake' },
              { Icon: Eye, label: 'Reserve Visibility' },
              { Icon: Scale, label: 'Governance Separation' },
              { Icon: Users, label: 'Defined Participant Roles' },
              { Icon: Network, label: 'Structured Collateral Pathways' },
            ].map(({ Icon, label }) => (
              <div
                key={label}
                data-testid={`lp-principle-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex flex-col items-center text-center p-5 sm:p-6 rounded-xl transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.12em] leading-tight" style={{ color: colors.white }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Important message */}
          <div
            className="max-w-3xl mx-auto rounded-2xl p-6 sm:p-8 text-center"
            style={{
              background: 'linear-gradient(180deg, rgba(0,128,128,0.07) 0%, rgba(0,128,128,0.02) 100%)',
              border: `1px solid ${colors.teal}26`,
            }}
          >
            <p className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.85 }}>
              Digital instruments operate as <span style={{ color: colors.teal, fontWeight: 500 }}>interaction layers</span>. They are not the primary proposition.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* §4 THE RESERVE FRAMEWORK — pillars                            */}
      {/* ============================================================ */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl px-6 sm:px-10 lg:px-16 py-14 sm:py-16 lg:py-20"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 30px 60px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
          >
            <SectionHeading
              eyebrow="The Reserve Framework"
              title="THE TRUST LAYER BEHIND THE ECOSYSTEM."
              sub="The reserve framework provides the structure through which assets are reviewed, recorded, monitored, and connected to liquidity pathways."
            />

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-12">
              {frameworkPillars.map(({ Icon, label }) => (
                <div
                  key={label}
                  data-testid={`lp-pillar-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex flex-col items-center text-center p-5 sm:p-6 rounded-xl transition-all duration-500"
                  style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}40`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
                >
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                    <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] leading-tight" style={{ color: colors.white }}>{label}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => navigate('/reserve-architecture')}
                data-testid="lp-framework-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.teal, border: `1px solid ${colors.teal}` }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.teal}14`; e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                Understand The Reserve Framework
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* §5 WHAT LIQUIDITY PROVIDERS CAN EVALUATE                      */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.65) 50%, #050505 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Designed For Institutional Review"
            title="WHAT LIQUIDITY PROVIDERS CAN EVALUATE."
            sub="The reserve framework is structured to support independent diligence across composition, verification, governance, and marketplace separation."
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {evaluationCards.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`lp-evaluation-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group p-7 sm:p-8 rounded-2xl h-full flex flex-col transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}40`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 20px 40px -20px ${colors.teal}40`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 leading-tight tracking-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* §6 WHY THE STRUCTURE MATTERS — comparison                     */}
      {/* ============================================================ */}
      <section className="py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why The Structure Matters"
            title="BUILT FOR STRUCTURE, NOT SPECULATION."
            sub="Arizore is designed around reserve visibility, collateral quality, and disciplined participation. The reserve framework provides a clearer structure for connecting verified asset contributors and liquidity partners."
          />

          <div
            className="rounded-2xl overflow-x-auto"
            style={{
              border: '1px solid rgba(255,255,255,0.06)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              boxShadow: '0 30px 60px -30px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
            data-testid="lp-comparison-table"
          >
            <table className="w-full text-left min-w-[640px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.teal}20` }}>
                  <th className="py-5 px-5 sm:px-7 text-xs uppercase tracking-[0.16em] font-semibold align-bottom" style={{ color: colors.white, opacity: 0.7, width: '50%' }}>
                    Traditional Asset Environment
                  </th>
                  <th className="py-5 px-5 sm:px-7 text-xs uppercase tracking-[0.16em] font-semibold align-bottom relative" style={{ color: colors.white, width: '50%', backgroundColor: `${colors.teal}10`, borderLeft: `1px solid ${colors.teal}50` }}>
                    Arizore Reserve Framework
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, idx) => (
                  <tr key={row[0]} style={{ borderBottom: idx === comparisonRows.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.05)' }}>
                    <td className="py-5 px-5 sm:px-7 align-top">
                      <div className="flex items-start gap-3">
                        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.white, opacity: 0.35 }} strokeWidth={1.5} />
                        <span className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.55 }}>{row[0]}</span>
                      </div>
                    </td>
                    <td className="py-5 px-5 sm:px-7 align-top" style={{ backgroundColor: `${colors.teal}08`, borderLeft: `1px solid ${colors.teal}40` }}>
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.teal }} strokeWidth={1.5} />
                        <span className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.9 }}>{row[1]}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* §8 PARTNER & GOVERNANCE LAYER                                 */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.6) 50%, #050505 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Partner & Governance Layer"
            title="SEPARATION BUILDS TRUST."
            sub="Arizore’s ecosystem is supported by defined participant roles, technical review processes, infrastructure partners, and governance separation. The objective is to create a framework that is understandable, auditable, and credible for institutional participants."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-12">
            {[
              { Icon: Landmark, title: 'Black Tie / BTX', body: 'Marketplace operator. Independent from reserve infrastructure.' },
              { Icon: ClipboardCheck, title: 'Technical Review', body: 'Independent review of reserve assets and reporting standards.' },
              { Icon: Network, title: 'Infrastructure', body: 'Independent infrastructure providers supporting reserve operations.' },
              { Icon: Scale, title: 'Governance Separation', body: 'Corporate, reserve, marketplace, and interaction layers operate distinctly.' },
              { Icon: ShieldCheck, title: 'Reserve Integrity', body: 'Verification, reporting, and visibility maintained as foundational pillars.' },
            ].map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`lp-governance-${title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                className="p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.12em] mb-2 leading-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-xs sm:text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => navigate('/reserve-data/azg')}
              data-testid="lp-governance-cta-reserve-data"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.teal, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.teal}14`; e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              View Reserve Data
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* §9 HOW ENGAGEMENT WORKS                                       */}
      {/* ============================================================ */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="How Engagement Works"
            title="A STRUCTURED CONVERSATION, NOT A SALES FUNNEL."
            sub="Engagement follows a deliberate sequence designed for institutional review."
          />

          <ol className="relative space-y-3">
            <div aria-hidden="true" className="absolute left-7 sm:left-8 top-7 bottom-7 w-px" style={{ background: `linear-gradient(180deg, transparent 0%, ${colors.teal}50 8%, ${colors.teal}50 92%, transparent 100%)` }} />
            {engagementSteps.map(({ step, title, body, Icon }) => (
              <li
                key={step}
                data-testid={`lp-engagement-step-${step}`}
                className="relative flex items-start gap-5 sm:gap-6 p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div
                  className="relative z-10 flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#070707', border: `1px solid ${colors.teal}50`, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.03), 0 0 0 4px rgba(4,4,4,1)' }}
                >
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <div className="flex-grow pt-1">
                  <span className="block text-[10px] uppercase tracking-[0.28em] font-medium mb-1.5" style={{ color: colors.teal, opacity: 0.85 }}>Step {step}</span>
                  <h3 className="text-base sm:text-lg font-semibold tracking-tight mb-2" style={{ color: colors.white }}>{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============================================================ */}
      {/* §10 GET IN TOUCH                                              */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.09 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.75) 50%, #040404 100%)' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Get In Touch"
            title="EXPLORE VERIFIED COLLATERAL OPPORTUNITIES."
            sub="If you are a liquidity provider, structured capital partner, fund, or institution exploring verified collateral opportunities, speak with the Arizore team."
          />

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={openFilloutPopup}
              data-testid="lp-get-in-touch-speak-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Speak With The Team
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/reserve-architecture')}
              data-testid="lp-get-in-touch-architecture-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            >
              View Reserve Architecture
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LiquidityProvidersPage;
